import {
  Control,
  FieldErrors,
  FieldValues,
  UseFormSetValue,
} from 'react-hook-form';
import { HookFormComponentProps } from 'ui-component/HookFormComponents/types';
import { ReactNode, useState } from 'react';
import ContextualCreateButton from 'ui-component/ContextualCreateButton';
import { ALL_APPS } from 'constants/appConstants';
import { SxProps } from '@mui/system';
import { Theme } from '@mui/material/styles';
import { OrgSupplier } from 'types/suppliers';
import { appPermissionAccessLevels } from 'types/apps';
import { HookQuerySearchSelect } from 'ui-component/HookFormComponents/HookQuerySearchSelect';
import { useGetOrgSuppliersServerSideQuery } from 'store/slices/apiV1/suppliers';
import CreateEditOrgSupplierDialog from 'views/suppliers/CreateEditOrgSupplierDialog';

type HookFormOrgSupplierAutocompleteProps = {
  children?: ReactNode;
  defaultValue?: string | string[];
  disabled?: boolean;
  multiple?: boolean;
  sx?: SxProps<Theme>;
  helperText?: string;
  mt?: number;
  fullWidth?: boolean;
  setValue: UseFormSetValue<FieldValues>;
  [props: string]: unknown;
  errors: FieldErrors;
  control: Control;
  defaultSupplierObjectId?: OrgSupplier['objectId'];
} & HookFormComponentProps;

export const HookFormOrgSupplierAutocomplete = ({
  errors,
  control,
  name = '',
  label,
  defaultValue,
  disabled,
  multiple = false,
  sx,
  mt = 2,
  helperText,
  fullWidth = true,
  defaultSupplierObjectId,
  setValue,
  ...rest
}: HookFormOrgSupplierAutocompleteProps) => {
  const [newOrgSupplierDialogOpen, setNewOrgSupplierDialogOpen] =
    useState(false);
  const [contextuallyCreatedOption, setContextuallyCreatedOption] =
    useState<OrgSupplier | null>(null);

  return (
    <>
      <ContextualCreateButton
        onClick={() => setNewOrgSupplierDialogOpen(true)}
        permissionScope={{
          app: ALL_APPS.SUPPLIERS.id,
          accessLevel: appPermissionAccessLevels.edit,
        }}
      >
        <HookQuerySearchSelect<OrgSupplier>
          name={name}
          label={label}
          useGetQuery={useGetOrgSuppliersServerSideQuery}
          errors={errors}
          control={control}
          schema={['name']}
          getOptionLabel={(option: OrgSupplier) => option.name}
          fullWidth
          mb={0}
          contextuallyCreatedOption={contextuallyCreatedOption || undefined}
          filters={
            defaultSupplierObjectId
              ? [
                  {
                    field: 'objectId',
                    operator: '=',
                    value: defaultSupplierObjectId,
                  },
                ]
              : undefined
          }
          autoSelectFirst={!!defaultSupplierObjectId}
        />
      </ContextualCreateButton>
      <CreateEditOrgSupplierDialog
        dialogOpen={newOrgSupplierDialogOpen}
        onClose={() => setNewOrgSupplierDialogOpen(false)}
        setSelectedSupplier={(value) => {
          setContextuallyCreatedOption(value);
          setValue && setValue(name, value.id);
        }}
      />
    </>
  );
};

export default HookFormOrgSupplierAutocomplete;
